import {CA} from '../../../../shared/scripts/analytics/Analytics'
import {SocketLobbySend} from '../../../../shared/scripts/socket/SocketLobbySend'
import {Sound} from '../../../../shared/scripts/Sound'
import {UIWAdController} from '../../ui_ad/UIWAdController'
import {ViewPopupLoginIncentive} from '../../../../shared/scripts/ui_popup/ViewPopupLoginIncentive'
import {throttleUserAction} from '../../../../shared/scripts/ui_utils/ClientUtils'
import {RoomType} from '@colonist/lobby-types'
import {ClientUserState} from '../../../../shared/scripts/ClientUserState'
import {UIUtilsEjsVariables} from '../../../../shared/scripts/UIUtilsEjsVariables'
import {UIManager} from '../UIManager'
import {UIDiscordInstance} from '../../discord/child_iframe/UIDiscordInstance'
import {PlayerEnums} from '../../../../../shared/GameEnums'
import {SocketDataTypes} from '../../../../../shared/SocketDataTypes'
import {clientI18n} from '../../../../shared/scripts/ClientI18n'
import {setElementInnerHTML} from '../../../../shared/scripts/ui_utils/DomUtils'
import {SDKRequest} from '../../../../../shared/DiscordActivityTypes'
import {UIDChildIFrameInstanceInit} from '../../discord/child_iframe/UIDChildIFrameInstanceInit'

export class UIHomepageCTAController {
    private readonly uiManager: UIManager

    private readonly mainCTA: HTMLLinkElement
    private readonly createCTA: HTMLLinkElement
    private readonly botsCTA: HTMLLinkElement

    private triggerInviteFriendsPopup: boolean = false

    constructor(uiManager: UIManager) {
        this.uiManager = uiManager
        this.mainCTA = document.getElementById('homepage_cta_main') as HTMLLinkElement
        this.createCTA = document.getElementById('homepage_cta_create') as HTMLLinkElement
        this.botsCTA = document.getElementById('homepage_cta_playvsbots') as HTMLLinkElement

        if(ClientUserState.userState?.isBeginner()) {
            setElementInnerHTML(this.botsCTA, {key: 'strings:homePage.ctas.singlePlayer'})
        }

        this.mainCTA.onclick = () => this.mainCTAAction()
        this.createCTA.onclick = throttleUserAction(() => this.createCTAAction())

        this.botsCTA.onclick = throttleUserAction(() => {
            Sound.playSound(Sound.SoundType.Click)
            CA.sendGoogleAnalyticsEvent(CA.Category.Lobby, CA.Label.ClickedVSBots)

            if(ClientUserState.userState?.isBeginner()) {
                this.uiManager.matchmakingController.selectMatchPopupView.showSinglePlayerOptions()
                this.uiManager.matchmakingController.selectMatchPopupView.show()
                return
            }

            this.uiManager.matchmakingController.selectMatchPopupView.showAllBotOptions()
            this.uiManager.matchmakingController.selectMatchPopupView.show()
        })
    }

    mainCTAAction = () => {
        Sound.playSound(Sound.SoundType.Click)
        CA.sendGoogleAnalyticsEvent(CA.Category.Lobby, CA.Label.ClickedLobbyMainCTA)

        UIHomepageCTAController.showLoginIncentivePopupWrapper(() => {
            UIWAdController.initiatePopupActionFromUserState(() => {
                this.uiManager.matchmakingController.showMatchmakingMatchTypeSelector()
            }, this.uiManager.adController.adblockState.isAdblockActive)
        })
    }

    createCTAAction = () => {
        Sound.playSound(Sound.SoundType.Click)
        CA.sendGoogleAnalyticsEvent(CA.Category.Lobby, CA.Label.ClickedCreateRoom)

        if(UIUtilsEjsVariables.isUserOnDiscord) {
            const channelId = UIDiscordInstance.getDiscordInstance().voiceChannelId
            SocketLobbySend.clickedJoinChannelRoomOrGame(channelId, RoomType.DiscordActivity)

            if(UIHomepageCTAController.shouldTriggerInviteFriendsPopup(this.triggerInviteFriendsPopup, UIDiscordInstance.getDiscordInstance())) UIDiscordInstance.getDiscordInstance().sendSDKRequest(SDKRequest.InviteFriends)
            return
        }

        UIHomepageCTAController.showLoginIncentivePopupWrapper(() => {
            UIWAdController.initiatePopupActionFromUserState(() => {
                void SocketLobbySend.clickedCreateRoom(RoomType.Standard)
            }, this.uiManager.adController.adblockState.isAdblockActive)
        })
    }

    static showLoginIncentivePopupWrapper(clickAction: () => void) {
        if(ClientUserState.userState?.isLoggedIn) {
            clickAction()
            return
        }

        if(ClientUserState.userState?.hasZeroCompletedGames() !== false) {
            clickAction()
            return
        }
        Sound.playSound(Sound.SoundType.Click)
        new ViewPopupLoginIncentive(() => undefined, () => undefined, () => {
            clickAction()
        })
    }

    updateDiscordCTAContent(roomData: SocketDataTypes.ChannelRoomUpdate) {
        if(!UIUtilsEjsVariables.isUserOnDiscord) throw new Error('This should only be called on the discord integration server')

        setElementInnerHTML(this.createCTA, {key: 'strings:colonistCommon.utils.emptyString'})

        this.createCTA.innerText = clientI18n.t('strings:homePage.ctas.play.title', {context: 'web'})
        this.createCTA.onclick = () => this.mainCTAAction()
        this.mainCTA.onclick = throttleUserAction(() => this.createCTAAction())

        this.triggerInviteFriendsPopup = false

        if(roomData.type == PlayerEnums.ChannelStatus.Closed) {
            this.mainCTA.innerText = clientI18n.t('strings:homePage.ctas.play.title', {context: 'discord'})
            this.triggerInviteFriendsPopup = true
            return
        }

        if(roomData.type == PlayerEnums.ChannelStatus.Game) {
            this.mainCTA.innerText = clientI18n.t('strings:homePage.ctas.spectate')
            return
        }

        if(roomData.type == PlayerEnums.ChannelStatus.Reconnect) {
            this.mainCTA.innerText = clientI18n.t('strings:homePage.ctas.rejoinGame')
            return
        }

        const playersInRoom = roomData.playersInRoom
        const maxPlayers = roomData.maxPlayers
        const isGameFull = playersInRoom >= maxPlayers

        if(isGameFull) {
            this.mainCTA.innerText = clientI18n.t('strings:homePage.ctas.roomFull')
            return
        }

        this.mainCTA.innerText = clientI18n.t('strings:homePage.ctas.joinRoom')
    }

    static shouldTriggerInviteFriendsPopup(triggerInviteFriendsPopup: boolean, discordInstance: UIDChildIFrameInstanceInit): boolean {
        if(!triggerInviteFriendsPopup) return false
        if(!discordInstance.inDiscordServer) return false
        if(!discordInstance.canInviteFriends) return false
        return true
    }
}
